// Settings → Privacy as the Application API serves it: the person's consents and rights requests, and for an
// Owner the business's forwarded requests, the reviewer-access log and offboarding (M2-41). Dates are ISO 8601.
import { call, refusal, type Fetch, type Refusal } from "../http.js";

/** What a person consents to, in the order Settings → Privacy lists them (their words are in the catalogue). */
export const CONSENT_PURPOSES = ["account", "sensitive_data", "quality_monitoring", "product_news"] as const;
export type ConsentPurpose = (typeof CONSENT_PURPOSES)[number];

/** The rights a person can ask for, in the order the request form offers them. */
export const RIGHTS = ["access", "correction", "erasure", "withdraw_consent", "nominate", "grievance"] as const;
export type Right = (typeof RIGHTS)[number];

export interface Consent {
  readonly purpose: ConsentPurpose;
  readonly given: boolean;
  readonly noticeVersion: string | null;
  readonly channel: string | null;
  readonly at: string | null;
  /** Given under an older notice. */
  readonly stale: boolean;
}

export type RequestStatus = "awaiting_code" | "verified" | "forwarded" | "completed" | "refused";

export interface RightsRequest {
  readonly reference: string;
  readonly right: Right;
  readonly about: "account" | "business";
  readonly detail: string;
  readonly status: RequestStatus;
  readonly receivedAt: string;
  readonly forwardBy: string | null;
  readonly dueBy: string;
  readonly forwardedTo: "tenant" | "lamp" | null;
  readonly forwardedAt: string | null;
  readonly completedAt: string | null;
  readonly outcome: string | null;
}

/** `GET /api/privacy`: what the person sees in Settings → Privacy. */
export interface PrivacyOverview {
  readonly noticeVersion: string;
  readonly consents: readonly Consent[];
  readonly requests: readonly RightsRequest[];
}

export interface RequestReceipt {
  readonly reference: string;
  readonly acknowledgedAt: string;
  readonly dueBy: string;
}

export interface NewRightsRequest {
  readonly identifier: { readonly kind: "email" | "mobile"; readonly value: string };
  readonly right: Right;
  readonly about: "account" | "business";
  readonly detail: string;
}

export interface ReviewerAccessEntry {
  readonly sequence: number;
  readonly staff: string;
  readonly subject: string;
  readonly unmasked: boolean;
  readonly reason: string;
  readonly evalCase: string | null;
  readonly sharedCase: boolean;
  readonly sourceChannel: string | null;
  readonly at: string;
}

export type OffboardingConfirmation = "exported" | "sealed_fields" | "access_ends" | "irreversible";

/** `GET /api/privacy/offboarding`: where offboarding stands, with the exact words to confirm. */
export interface Offboarding {
  readonly stage: "none" | "exporting" | "shredded";
  readonly exportUntil: string | null;
  readonly confirmed: readonly OffboardingConfirmation[];
  readonly archive: "keep" | "none" | null;
  readonly missing: readonly string[];
  readonly confirmations: Readonly<Record<OffboardingConfirmation, string>>;
  readonly archiveChoices: Readonly<Record<"keep" | "none", string>>;
}

export interface PrivacyApi {
  overview(): Promise<PrivacyOverview>;
  giveConsent(purpose: ConsentPurpose): Promise<readonly Consent[]>;
  withdrawConsent(purpose: ConsentPurpose): Promise<readonly Consent[]>;
  request(input: NewRightsRequest): Promise<RequestReceipt>;
  verify(reference: string, code: string): Promise<RightsRequest>;
  /** An Owner's open business-data requests. */
  tenantRequests(): Promise<{ readonly open: readonly RightsRequest[]; readonly overdue: number }>;
  completeTenantRequest(reference: string, outcome: string, refused: boolean): Promise<RightsRequest>;
  reviewerAccess(): Promise<readonly ReviewerAccessEntry[]>;
  offboarding(): Promise<Offboarding>;
  startOffboarding(): Promise<Offboarding>;
  confirmOffboarding(confirmation: OffboardingConfirmation): Promise<Offboarding>;
  chooseArchive(archive: "keep" | "none"): Promise<Offboarding>;
  cancelOffboarding(): Promise<Offboarding>;
  shred(): Promise<Offboarding>;
  /** The business's export, for an Owner who stepped up for `export.full`: a zip and its file name. */
  exportData(): Promise<ExportFile>;
}

export interface ExportFile {
  readonly name: string;
  readonly zip: Blob;
}

/** Where the Owner downloads the export: a zip of CSV and JSON files, after a step-up for each download. */
export const EXPORT_PATH = "/api/privacy/export";

/** The file name the api gives the export, or a plain one when it gives none. */
const fileName = (disposition: string | null) => /filename="([^"]+)"/.exec(disposition ?? "")?.[1] ?? "lamp-export.zip";

const requestPath = (reference: string) => `/api/privacy/requests/${encodeURIComponent(reference)}`;

/** Writes go as JSON, even with nothing to say: the Application API takes no other kind. */
export function createPrivacyApi(fetcher: Fetch): PrivacyApi {
  const consents = async (path: string, purpose: ConsentPurpose) =>
    (await call<{ consents: Consent[] }>(fetcher, path, { json: { purpose, channel: "web" } })).consents;
  return {
    overview: () => call<PrivacyOverview>(fetcher, "/api/privacy"),
    giveConsent: (purpose) => consents("/api/privacy/consents", purpose),
    withdrawConsent: (purpose) => consents("/api/privacy/consents/withdraw", purpose),
    request: (input) => call<RequestReceipt>(fetcher, "/api/privacy/requests", { json: { ...input, channel: "web" } }),
    verify: async (reference, code) =>
      (await call<{ request: RightsRequest }>(fetcher, `${requestPath(reference)}/verify`, { json: { code } })).request,
    tenantRequests: () => call(fetcher, "/api/privacy/tenant-requests"),
    completeTenantRequest: async (reference, outcome, refused) =>
      (
        await call<{ request: RightsRequest }>(
          fetcher,
          `/api/privacy/tenant-requests/${encodeURIComponent(reference)}/complete`,
          { json: { outcome, refused } },
        )
      ).request,
    reviewerAccess: async () =>
      (await call<{ entries: ReviewerAccessEntry[] }>(fetcher, "/api/privacy/reviewer-access")).entries,
    offboarding: () => call<Offboarding>(fetcher, "/api/privacy/offboarding"),
    startOffboarding: () => call<Offboarding>(fetcher, "/api/privacy/offboarding/start", { json: {} }),
    confirmOffboarding: (confirmation) =>
      call<Offboarding>(fetcher, "/api/privacy/offboarding/confirmations", { json: { confirmation } }),
    chooseArchive: (archive) => call<Offboarding>(fetcher, "/api/privacy/offboarding/archive", { json: { archive } }),
    cancelOffboarding: () => call<Offboarding>(fetcher, "/api/privacy/offboarding/cancel", { json: {} }),
    shred: () => call<Offboarding>(fetcher, "/api/privacy/offboarding/shred", { json: {} }),
    async exportData() {
      const response = await fetcher(EXPORT_PATH, { credentials: "same-origin" });
      if (!response.ok) throw refusal(response.status, (await response.json().catch(() => ({}))) as Refusal);
      return { name: fileName(response.headers.get("content-disposition")), zip: await response.blob() };
    },
  };
}
